import React, { useEffect, useRef, useState } from 'react';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { saveTransaction } from '@/data/contributions';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ModalWrapper from '@/components/General/ModalWrapper';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Checkbox from '@/components/General/inputs/Checkbox';
import { Text, View, Pressable, Platform } from 'react-native';
import Callout from '@/components/General/Callout';
import Toggle from '@/components/General/inputs/Toggle';
import { StyleSheet } from 'react-native-unistyles';
import InputSection from '@/components/General/inputs/InputSection';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const roundCents = (value) => {
    return Math.round((Number(value) || 0) * 100) / 100;
};

const parseCurrency = (value) => {
    return roundCents(String(value ?? 0).replace(/,/g, ''));
};

const TransactionForm = () => {
    const [transactionDetails, setTransactionDetails] = useState({});
    const [focusedFundIndex, setFocusedFundIndex] = useState(null);
    const [openPayee, setOpenPayee] = useState(false);
    const initializedRef = useRef(false);

    const { id, batchID } = useLocalSearchParams()
  
  
    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: transactionToEdit } = Query.contributionTransactions.byId({id: id, options: {enabled: !!id}})
    const {data: funds} = Query.contributionFunds.all()
    const { data: batch } = Query.contributionBatches.byId({ id: batchID, options: { enabled: !!batchID } });
    const isReachGiving = batch?.isReachGiving ?? false;
    const router = useRouter()

    useEffect(() => {
        if (isReachGiving && !id) {
            Toast.show({ type: 'error', text2: 'Cannot add transactions to a Reach Giving batch' });
            router.canGoBack() ? router.back() : router.replace('/');
        }
    }, [isReachGiving, id]);

    useEffect(() => {
        if(transactionToEdit && id){
            setTransactionDetails({
                ...transactionToEdit,
                isAnonymous: !transactionToEdit.personID,
                amount: Number(transactionToEdit.amount).toFixed(2),
                Funds: transactionToEdit.Funds?.map(fund => ({...fund.Fund, amount: Number(fund.amount).toFixed(2)})),
                originalValues: transactionToEdit,
            })
        }
    }, [transactionToEdit, id])

    useEffect(() => {
        if(batchID && funds && !id && !initializedRef.current){
            initializedRef.current = true;
            setTransactionDetails(prev => ({
                ...prev,
                batchID: batchID,
                transactionDate: new Date(),
                Funds: funds.filter(fund => fund.isDefault)
            }))
        }
    }, [batchID, funds])
  
    const saveTransactionMutation = useMutation({
        mutationFn: saveTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.contributionTransactions.byId(id)});
            queryClient.invalidateQueries({queryKey: ['contributions', 'batches']});
            queryClient.invalidateQueries({queryKey: ['contributions', 'transactions']});

            setTimeout(() => Toast.show({ type: 'success', text2: `Transaction ${id ? "updated" : "created"} successfully!` }), 100);
            
            if(!id){
                setTransactionDetails(prev => ({
                    ...prev,
                    amount: '',
                    personID: '',
                    Person: null,
                    isAnonymous: false,
                    Funds: funds.filter(fund => fund.isDefault).map(f => ({...f, amount: ''})),
                    methodDetailValue: '',
                    note: '',
                }));

                if (Platform.OS === 'web') {
                    setOpenPayee(true);
                }
            }
            else{
                router.canGoBack() ? router.back() : router.replace("/")
            }
        },
        onError: () => {
            setTimeout(() => Toast.show({ type: 'error', text2: `Failed to ${id ? "update" : "create"} transaction!` }), 100);
        },
    });

    const fundAmountInputs = transactionDetails?.Funds?.map((fund, i) => {
        const totalOtherAmounts = transactionDetails?.Funds?.filter(f => f.id != fund.id).reduce((total, fund) => total + parseCurrency(fund.amount), 0);
        const fundsLeft = roundCents(parseCurrency(transactionDetails?.amount) - totalOtherAmounts);

        return (
            <View key={i}>
                <Input
                    key={i}
                    type="decimal"
                    format="currency"
                    title={fund.name}
                    placeholder={`Amount for ${fund.name}`}
                    prefixIcon="cash-outline"
                    value={fund.amount}
                    onUpdate={(new_value) => {
                        setTransactionDetails({
                            ...transactionDetails,
                            Funds: transactionDetails.Funds.map((f, j) => j == i ? {...f, amount: new_value} : f)
                        })
                    }}
                    onFocus={() => setFocusedFundIndex(i)}
                />
                {(focusedFundIndex == i && fundsLeft != parseCurrency(fund.amount) && fundsLeft > 0) &&
                    <Pressable onPress={() => {
                        const remainingAmount = roundCents(parseCurrency(transactionDetails?.amount) - totalOtherAmounts);

                        setFocusedFundIndex(null)
                        setTransactionDetails({
                            ...transactionDetails,
                            Funds: transactionDetails.Funds.map((f, j) => j == i ? {...f, amount: remainingAmount.toFixed(2).toString()} : f)
                        })
                    }}>
                        <Text style={styles.remainingAmountText}>Apply Remaining ${fundsLeft.toFixed(2)}</Text>
                    </Pressable>
                }
            </View>
        )
    })

    const totalAmount = transactionDetails?.Funds?.reduce((total, fund) => total + parseCurrency(fund.amount), 0);

    const submit = () => {
        if(!transactionDetails?.amount || parseCurrency(transactionDetails?.amount) <= 0){
            Toast.show({type: "error", text1: "Missing required field!", text2: "Amount is required and must be greater than 0"});
            return;
        }

        if(!transactionDetails?.isAnonymous && !transactionDetails?.personID){
            Toast.show({type: "error", text1: "Missing required field!", text2: "Payee is required"});
            return;
        }

        if(!transactionDetails?.transactionDate){
            Toast.show({type: "error", text1: "Missing required field!", text2: "Transaction Date is required"});
            return;
        }

        if(!transactionDetails?.Funds?.length){
            Toast.show({type: "error", text1: "Missing required field!", text2: "At least one Fund is required"});
            return;
        }

        const cleanAmount = parseCurrency(transactionDetails?.amount);
        let funds = transactionDetails?.Funds?.map(f => ({
            ...f,
            amount: parseCurrency(f.amount)
        }));

        if(funds?.length === 1){
            funds[0].amount = cleanAmount;
        }
        else if(funds?.length > 1 && roundCents(totalAmount ?? 0) != cleanAmount){
            Toast.show({type: "error", text2: "Total Amount does not match the total amount in funds!"});
            return;
        }

        const { originalValues, ...cleanTx } = transactionDetails;
        saveTransactionMutation.mutate({
            ...cleanTx,
            personID: cleanTx?.isAnonymous ? null : cleanTx?.personID,
            amount: cleanAmount,
            Funds: funds,
            originalValues,
        });
    }

  return (
    <ModalWrapper title={id ? "Edit Transaction" : "New Transaction"} loading={saveTransactionMutation.isPending} onConfirm={() => submit()} confirmText={id ? "Update" : "Create"}>
        {transactionDetails?.Funds?.length > 1 && roundCents(totalAmount ?? 0) != roundCents(parseCurrency(transactionDetails?.amount)) && <Callout type="error" message="Total Amount does not match transaction amount" />}
        
        <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <SheetSelector
                title="Payee"
                type="people"
                required={!transactionDetails?.isAnonymous}
                disabled={!!transactionDetails?.isAnonymous || isReachGiving}
                value={transactionDetails?.Person}
                onChange={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        personID: new_value?.id,
                        Person: new_value
                    })
                }}
                placeholder="Select a Person"
                open={openPayee}
                onOpen={() => setOpenPayee(true)}
                onClose={() => setOpenPayee(false)}
            />

            <View style={styles.checkboxRow}>
                <Checkbox
                    checked={transactionDetails?.isAnonymous}
                    disabled={isReachGiving}
                    onChange={(checked) => {
                        setTransactionDetails(prev => ({
                            ...prev,
                            isAnonymous: checked,
                            personID: checked ? null : prev.personID,
                            Person: checked ? null : prev.Person
                        }))
                    }}
                />
                <Text style={styles.checkboxLabel}>Anonymous Gift</Text>
            </View>

            <Input
                type="decimal"
                format="currency"
                required
                title="Total Amount"
                prefixIcon="cash-outline"
                disabled={isReachGiving}
                value={transactionDetails?.amount}
                onUpdate={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        amount: new_value
                    })
                }}
            />

            <SheetSelector
                title="Funds"
                type="contributionFunds"
                multiple
                noDefaultTruncated
                onChange={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        Funds: new_value
                    })
                }}
                value={transactionDetails?.Funds || []}
            />

            {transactionDetails?.Funds?.length > 1 &&
                <InputSection title="Fund Splits">
                    {fundAmountInputs}
                </InputSection>
            }

            <SheetSelector
                title="Method"
                type="contributionMethods"
                onChange={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        methodID: new_value?.id,
                        Method: new_value
                    })
                }}
                value={transactionDetails?.Method}
            />

            {transactionDetails?.Method?.detailName && 
                <Input
                    title={transactionDetails?.Method?.detailName}
                    value={transactionDetails?.methodDetailValue}
                    onUpdate={(new_value) => {
                        setTransactionDetails({
                            ...transactionDetails,
                            methodDetailValue: new_value
                        })
                    }}
                    placeholder={transactionDetails?.Method?.detailName}
                />
            }

            <DatePickerInput
                title="Transaction Date"
                required
                disabled={isReachGiving}
                value={transactionDetails?.transactionDate}
                tabIndex={2}
                onUpdate={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        transactionDate: new_value
                    })
                }}
            />

            <Input
                title="Note"
                multiline
                value={transactionDetails?.note}
                onUpdate={(new_value) => {
                    setTransactionDetails({
                        ...transactionDetails,
                        note: new_value
                    })
                }}
            />

            <View style={styles.switchContainer}>
                <Toggle
                    value={transactionDetails?.isTaxDeductible ?? true}
                    onValueChange={(new_value) => {
                        setTransactionDetails({
                            ...transactionDetails,
                            isTaxDeductible: new_value
                        })
                    }}
                />
                <Text style={styles.switchLabel}>Is Tax Deductible?</Text>
            </View>
        </KeyboardAwareScrollView>
    </ModalWrapper>
  );
};

const styles = StyleSheet.create(({colors, fonts}) => ({
    switchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginVertical: 15,

    },
    switchLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text
    },
    remainingAmountText: {
        fontSize: 12,
        color: colors.primary,
        _web: {
            cursor: 'pointer',
            _hover: {
                textDecorationLine: 'underline'
            }
        }
    },
    checkboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 8,
        marginBottom: 15,
    },
    checkboxLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text
    }
}))

export default TransactionForm;
